/**
 * **Le mini-test de calage audio** — SPEC-UI §7.7 : « 8 beats à 90 BPM, taper Espace, l'offset
 * médian mesuré est proposé comme valeur », dans [−200 ; +200] ms par pas de 5.
 *
 * Ce module est la partie **pure** : des instants entrent, un décalage sort. L'écran
 * (`screens/Reglages/CalageAudio.tsx`) fait jouer les temps et capte les frappes ; il ne calcule
 * rien.
 *
 * ## Le signe, et pourquoi il est celui-là
 *
 * Le Montage corrige chaque frappe **à la capture** : `atMs = t − audioOffsetMs` ⟦A-76⟧. Un
 * joueur dont la chaîne audio retarde de 30 ms entend le temps 30 ms après qu'il est tombé, et
 * frappe 30 ms en retard : ses frappes sont à `beat + 30`. Pour lui rendre son intention, il
 * faut retrancher 30 — donc `audioOffsetMs = +30 = médiane(frappe − beat)`. Le décalage proposé
 * est la médiane signée des écarts « frappe moins temps le plus proche ». La médiane, pas la
 * moyenne : une frappe oubliée ou un doigt qui rate ne doivent pas tirer la valeur.
 *
 * ⟦A-22⟧ Rien ici ne touche un score : le résultat est un réglage du JOUEUR, appliqué par le
 * Montage avant la soumission, que la musique joue ou non.
 */

/** Le tempo du test — §7.7 : 90 BPM. */
export const BPM_TEST = 90;
/** Le nombre de temps joués — §7.7 : 8. */
export const BEATS_TEST = 8;
/** La période d'un temps à 90 BPM : 666,67 ms. */
export const PERIODE_TEST_MS = 60_000 / BPM_TEST;
/** Le temps laissé avant le premier beat : on ne frappe pas au clic de « Lancer ». */
export const ATTENTE_AVANT_MS = 1_000;
/** Les bornes et le pas du réglage (§7.7). */
export const OFFSET_MIN_MS = -200;
export const OFFSET_MAX_MS = 200;
export const OFFSET_PAS_MS = 5;
/** En deçà de ce nombre de frappes utiles, on ne propose rien : trois points ne font pas une mesure. */
export const FRAPPES_MIN = 4;

/** Les instants des huit temps, relatifs au départ du test. */
export function tempsDuTest(): number[] {
  return Array.from({ length: BEATS_TEST }, (_, i) => ATTENTE_AVANT_MS + i * PERIODE_TEST_MS);
}

/** Le réglage borné et aligné sur le pas de 5 ms. */
export function bornerOffset(ms: number): number {
  const pas = Math.round(ms / OFFSET_PAS_MS) * OFFSET_PAS_MS;
  return Math.max(OFFSET_MIN_MS, Math.min(OFFSET_MAX_MS, pas));
}

/**
 * L'écart de chaque frappe au temps le plus proche — signé : positif, on a frappé après le
 * temps. Une frappe à plus d'une demi-période de tout temps est **orpheline** et ignorée : ce
 * n'est pas un retard, c'est un raté.
 */
export function ecartsAuxTemps(temps: readonly number[], frappes: readonly number[]): number[] {
  const ecarts: number[] = [];
  for (const f of frappes) {
    let meilleur: number | null = null;
    for (const t of temps) {
      const d = f - t;
      if (meilleur === null || Math.abs(d) < Math.abs(meilleur)) meilleur = d;
    }
    if (meilleur !== null && Math.abs(meilleur) <= PERIODE_TEST_MS / 2) ecarts.push(meilleur);
  }
  return ecarts;
}

/** La médiane d'une suite non vide. */
export function mediane(valeurs: readonly number[]): number {
  const tri = [...valeurs].sort((a, b) => a - b);
  const m = Math.floor(tri.length / 2);
  return tri.length % 2 === 1 ? tri[m]! : (tri[m - 1]! + tri[m]!) / 2;
}

export interface MesureCalage {
  /** Le décalage à proposer, borné et sur le pas — ce qui ira dans `audioOffsetMs`. */
  offsetMs: number;
  /** La médiane brute, avant bornes, pour la dire au joueur. */
  medianeMs: number;
  /** Combien de frappes ont compté. */
  frappesUtiles: number;
}

/**
 * Le décalage proposé, ou `null` s'il n'y a pas assez de frappes utiles.
 *
 * `temps` et `frappes` sont dans le même repère (ms depuis le départ du test).
 */
export function offsetPropose(
  temps: readonly number[],
  frappes: readonly number[],
): MesureCalage | null {
  const ecarts = ecartsAuxTemps(temps, frappes);
  if (ecarts.length < FRAPPES_MIN) return null;
  const med = mediane(ecarts);
  return { offsetMs: bornerOffset(med), medianeMs: Math.round(med), frappesUtiles: ecarts.length };
}
